import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  browserSupportsWebAuthn,
  startRegistration,
} from "@simplewebauthn/browser";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Messages } from "primereact/messages";
import { ProgressSpinner } from "primereact/progressspinner";
import Container from "../../components/menu/Container";
import getUserPasskeys, { PasskeyCredencial } from "../../service/getUserPasskeys";
import postUserPasskeyRegistroOptions from "../../service/postUserPasskeyRegistroOptions";
import postUserPasskeyRegistroFinalizar from "../../service/postUserPasskeyRegistroFinalizar";
import deleteUserPasskey from "../../service/deleteUserPasskey";

const AZUL = "#183462";

const sugerirNomeDispositivo = (): string => {
  const ua = navigator.userAgent;
  const navegador = /Edg\//.test(ua)
    ? "Edge"
    : /Chrome\//.test(ua)
    ? "Chrome"
    : /Firefox\//.test(ua)
    ? "Firefox"
    : /Safari\//.test(ua)
    ? "Safari"
    : "Navegador";
  const sistema = /Windows/.test(ua)
    ? "Windows"
    : /Mac OS/.test(ua)
    ? "macOS"
    : /Android/.test(ua)
    ? "Android"
    : /iPhone|iPad/.test(ua)
    ? "iOS"
    : /Linux/.test(ua)
    ? "Linux"
    : "";
  return sistema ? `${navegador} no ${sistema}` : navegador;
};

const formatarData = (data?: string) => {
  if (!data) return "-";
  try {
    return new Date(data).toLocaleString();
  } catch {
    return data;
  }
};

export default function Page() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const msgs = useRef<Messages>(null);

  const [passkeys, setPasskeys] = useState<PasskeyCredencial[]>([]);
  const [loading, setLoading] = useState(true);
  const [cadastrando, setCadastrando] = useState(false);
  const [nomeDispositivo, setNomeDispositivo] = useState(sugerirNomeDispositivo());
  const suportado = browserSupportsWebAuthn();

  const showError = (detail: string) => {
    msgs.current?.clear();
    msgs.current?.show([
      { sticky: false, life: 8000, severity: "error", summary: t("erro"), detail, closable: false },
    ]);
  };

  const showSuccess = (detail: string) => {
    msgs.current?.clear();
    msgs.current?.show([
      { sticky: false, life: 6000, severity: "success", summary: t("sucesso"), detail, closable: false },
    ]);
  };

  const carregar = () => {
    setLoading(true);
    getUserPasskeys()
      .then((data) => setPasskeys(data))
      .catch((error) => showError(error.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adicionar = async () => {
    setCadastrando(true);
    try {
      const options = await postUserPasskeyRegistroOptions();
      const credential = await startRegistration({ optionsJSON: options });
      await postUserPasskeyRegistroFinalizar(credential, nomeDispositivo || sugerirNomeDispositivo());
      showSuccess(String(t("chaveAcessoAdicionadaSucesso")));
      setNomeDispositivo(sugerirNomeDispositivo());
      carregar();
    } catch (error: any) {
      if (error?.name !== "NotAllowedError") {
        showError(error?.message || String(t("erro")));
      }
    } finally {
      setCadastrando(false);
    }
  };

  const remover = (credencial: PasskeyCredencial) => {
    if (!window.confirm(String(t("confirmarRemoverChaveAcesso")))) return;
    deleteUserPasskey(credencial.id)
      .then(() => {
        showSuccess(String(t("chaveAcessoRemovidaSucesso")));
        carregar();
      })
      .catch((error) => showError(error.message));
  };

  return (
    <Container>
      <div className="row">
        <div className="col-lg-12 mb-3">
          <div className="d-sm-flex align-items-center justify-content-between mb-4">
            <h1 className="h3 mb-0 text-gray">
              <i className="pi pi-id-card" style={{ fontSize: "2.0rem" }}></i> {t("chaveAcesso")}
            </h1>
          </div>
        </div>
      </div>

      <div className="card">
        <Messages ref={msgs} />

        <p style={{ maxWidth: "620px", color: "#555" }}>{t("chaveAcessoInstrucao")}</p>

        {!suportado && (
          <p className="p-error">{t("chaveAcessoNaoSuportada")}</p>
        )}

        {suportado && (
          <>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                alignItems: "flex-end",
                flexWrap: "wrap",
                margin: "1.5rem 0",
              }}
            >
              <div className="flex-auto">
                <label className="font-bold block mb-2">{t("nomeDispositivo")}</label>
                <InputText
                  value={nomeDispositivo}
                  onChange={(e) => setNomeDispositivo(e.target.value)}
                  placeholder={String(t("nomeDispositivoPlaceholder"))}
                  style={{ width: "20rem" }}
                />
              </div>
              <Button
                label={String(t("adicionarChaveAcesso"))}
                icon="pi pi-plus"
                style={{ backgroundColor: AZUL }}
                loading={cadastrando}
                onClick={adicionar}
              />
            </div>

            {loading ? (
              <div style={{ padding: "2rem", textAlign: "center" }}>
                <ProgressSpinner style={{ width: "50px", height: "50px" }} />
              </div>
            ) : passkeys.length === 0 ? (
              <p style={{ color: "#777" }}>{t("nenhumaChaveAcessoCadastrada")}</p>
            ) : (
              <div>
                {passkeys.map((credencial) => (
                  <div
                    key={credencial.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0.75rem 0",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>
                        {credencial.nomeDispositivo || t("chaveAcesso")}
                      </div>
                      <small style={{ color: "#777" }}>
                        {t("chaveAcessoCadastradaEm")}: {formatarData(credencial.dataCriacao)}
                        {credencial.dataUltimoUso && (
                          <> · {t("chaveAcessoUltimoUso")}: {formatarData(credencial.dataUltimoUso)}</>
                        )}
                      </small>
                    </div>
                    <Button
                      label={String(t("remover"))}
                      icon="pi pi-trash"
                      severity="danger"
                      outlined
                      onClick={() => remover(credencial)}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "2.5rem" }}>
          <Button
            label={String(t("voltar"))}
            icon="pi pi-arrow-left"
            severity="secondary"
            outlined
            onClick={() => navigate("/home")}
          />
        </div>
      </div>
    </Container>
  );
}
